import type { SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { ClassModel, action, getEnv, register, types } from "@gadgetinc/mobx-quick-tree";
import { defaults } from "lodash";
import { DateTime } from "luxon";
import { serializeError } from "serialize-error";
import type { DeepReadonly } from "ts-essentials";
import { clock } from "./Clock";
import { EnvironmentSlug } from "./Environment";
import { GadgetDomainModelClass } from "./utils";

export interface IServerContractExpirationManager {
  addContract(contract: ServerContract): void;

  removeContract(contract: ServerContract): void;

  boot(): void;
}

export enum ServerContractStatusCode {
  // Everything is working/worked as expected
  Nominal = "Nominal",

  // The contract is in the process of being fulfilled
  Pending = "Pending",

  // Something went unexpectedly wrong
  Errored = "Errored",

  // We haven't started working the contract yet so we don't know if it is going to succeed
  NotYetStarted = "NotYetStarted",

  // The contract has expired based on the expiresIn field
  Expired = "Expired",
}

export type ServerContractStatus =
  | { code: ServerContractStatusCode.Nominal }
  | { code: ServerContractStatusCode.Pending; details?: any }
  | { code: ServerContractStatusCode.Expired; message?: string }
  | {
      code: ServerContractStatusCode.Errored;
      message: string;
      causedByUserland: boolean;
      causedByClient: boolean;
      details: any;
    }
  | { code: ServerContractStatusCode.NotYetStarted };

export const defaultPendingContractExpiration = 1800;

@register
export class ServerContractProgressDetails extends ClassModel({
  description: types.string,
  numSteps: types.number,
  currentStep: types.number,
}) {}

/**
 * Represents the status of a unit of work that the server is going to run to meet a contract with the client.
 *
 * Think "we will keep the npm dependencies from package.json installed to the ones you've specified" or "we will keep the indexes you need in the database" -- this thing says if Gadget the platform has actually accomplished that.
 * `ServerContract` often reports across many executions of that unit of work, so it reflects the *current* status of the work, reflecting if the server is keeping up to date with new changes the client has asked for.
 * This object doesn't actually do the work, but gives us a facility to share the server's status and progress with the client. See `workServerContract` in the server to see the thing that uses this for record keeping.
 */
@register
export class ServerContract extends GadgetDomainModelClass("ServerContract", {
  epoch: types.optional(types.number, 0),
  status: types.frozen<ServerContractStatus>(),
  startedAt: types.optional(types.number, () => DateTime.now().toSeconds()),
  expiresIn: types.optional(types.integer, 0),
  progress: types.maybeNull(types.string),
  progressDetails: types.maybeNull(ServerContractProgressDetails),
  traceId: types.maybeNull(types.string),
}) {
  get expiresAt() {
    return this.startedAt > 0 && this.expiresIn > 0 ? this.expiresIn + this.startedAt : 0;
  }

  get isExpired() {
    return this.status.code === ServerContractStatusCode.Expired;
  }

  get isWorking() {
    return this.status.code == ServerContractStatusCode.Pending || this.status.code == ServerContractStatusCode.NotYetStarted;
  }

  get isSuccessful() {
    return this.status.code == ServerContractStatusCode.Nominal;
  }

  get isCompleted() {
    return this.status.code == ServerContractStatusCode.Nominal || this.status.code == ServerContractStatusCode.Errored || this.isExpired;
  }

  get isErrored() {
    return this.status.code == ServerContractStatusCode.Errored || this.isExpired;
  }

  get hasStarted() {
    return this.status.code != ServerContractStatusCode.NotYetStarted;
  }

  durationElapsedString() {
    const startDateTime = DateTime.fromSeconds(this.startedAt);
    const now = clock.getTime();
    const elapsed = now.diff(startDateTime, "seconds").toObject();
    if (elapsed.seconds! <= 1) return "just now";
    return startDateTime.toRelative({ base: now });
  }

  /**
   * Set the progress of this contract.
   *
   * @param title a human-friendly description of what's currently happening in the unit of work
   * @param progress an optional percentage ranging from 0 - 100 specifying how complete the unit of work is.
   */
  @action
  setProgress(title: string, details?: SnapshotIn<ServerContractProgressDetails>) {
    this.progress = title;
    this.progressDetails = details ? ServerContractProgressDetails.create(details) : null;
  }

  @action
  setTraceId(traceId: string) {
    this.traceId = traceId;
  }

  @action
  setExpiresIn(expiresIn: number) {
    this.expiresIn = expiresIn;
  }

  @action
  markAsSucceeded() {
    this.status = { code: ServerContractStatusCode.Nominal };
  }

  @action
  markAsWorking(expiresIn = defaultPendingContractExpiration, details?: any) {
    this.startedAt = DateTime.now().toSeconds();
    this.expiresIn = expiresIn;
    this.status = { code: ServerContractStatusCode.Pending, details };
    this.epoch = this.epoch + 1;
    this.progress = null;
    return this.epoch;
  }

  @action
  markAsErrored(message: string, options: { causedByUserland?: boolean; causedByClient?: boolean; cause?: any; details?: any } = {}) {
    this.status = {
      code: ServerContractStatusCode.Errored,
      message,
      ...defaults(options, {
        causedByUserland: false,
        causedByClient: false,
        details: null,
      }),
    };
  }

  @action
  markAsExpired() {
    if (this.isWorking && this.expiresAt > 0 && DateTime.now().toSeconds() > this.expiresAt) {
      this.status = {
        code: ServerContractStatusCode.Expired,
        message: "contract has expired",
      };
    }
  }

  @action
  markWithError(error: Error | unknown) {
    if (isErrorLikeObject(error)) {
      const { message, causedByUserland, causedByClient, ...details } = error;
      this.markAsErrored(String(message) ?? String(details), {
        causedByUserland: causedByUserland === true,
        causedByClient: causedByClient === true,
        details: Object.keys(details).length > 0 ? serializeError(details) : null,
      });
    } else {
      this.markAsErrored(String(error));
    }
  }

  @action
  afterCreate() {
    const { serverContractManager } = getEnv<{ serverContractManager?: IServerContractExpirationManager }>(this);

    if (serverContractManager) {
      serverContractManager.addContract(this);
    }
  }

  @action
  beforeDestroy() {
    const { serverContractManager } = getEnv<{ serverContractManager: IServerContractExpirationManager }>(this);

    if (serverContractManager) {
      serverContractManager.removeContract(this);
    }
  }
}

export const NominalContractSnapshot: DeepReadonly<SnapshotIn<typeof ServerContract>> = {
  status: { code: ServerContractStatusCode.Nominal },
};

export const PendingContractSnapshot: DeepReadonly<SnapshotIn<typeof ServerContract>> = {
  status: { code: ServerContractStatusCode.Pending },
};

export const NotYetStartedContractSnapshot: DeepReadonly<SnapshotIn<typeof ServerContract>> = {
  status: { code: ServerContractStatusCode.NotYetStarted },
};

export const DefaultNominalServerContract = types.optional(ServerContract, NominalContractSnapshot);

@register
export class DeployContract extends ServerContract.extend({
  sourceEnvironment: types.maybeNull(types.model({ id: types.string, slug: types.frozen<EnvironmentSlug>() })),
  deployedByUser: types.maybeNull(types.model({ id: types.string, name: types.maybeNull(types.string), email: types.string })),
}) {
  @action
  setSourceEnvironment(environment?: { id: string; slug: EnvironmentSlug } | null) {
    if (!environment) {
      this.sourceEnvironment = null;
    } else {
      this.sourceEnvironment = environment;
    }
  }

  @action
  setDeployedByUser(user?: { id: string; name?: string | null; email: string } | null) {
    if (!user) {
      this.deployedByUser = null;
    } else {
      this.deployedByUser = {
        id: user.id,
        email: user.email,
        name: user.name ?? null,
      };
    }
  }

  @action
  setStartedAt(startedAt?: number | null) {
    this.startedAt = startedAt ?? DateTime.now().toSeconds();
  }
}

export const NominalDeployContractSnapshot: DeepReadonly<SnapshotIn<typeof DeployContract>> = {
  status: { code: ServerContractStatusCode.Nominal },
};

export const PendingDeployContractSnapshot: DeepReadonly<SnapshotIn<typeof DeployContract>> = {
  status: { code: ServerContractStatusCode.Pending },
};

export const NotYetStartedDeployContractSnapshot: DeepReadonly<SnapshotIn<typeof DeployContract>> = {
  status: { code: ServerContractStatusCode.NotYetStarted },
};

export const DefaultNominalDeployContract = types.optional(DeployContract, NominalDeployContractSnapshot);

export const isErrorLikeObject = (
  value: unknown
): value is {
  message?: unknown;
  causedByUserland?: unknown;
  causedByClient?: unknown;
} => typeof value === "object";
